import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@shared/utils';

/*
 * 顏色一律走語義 token，不用 Tailwind 內建色板：同一份 class 才能在亮暗兩個主題都成立。
 *
 * 註解裡不要寫出不使用的 class 名：Tailwind v4 以純文字掃描來源檔，
 * 註解中的 class 一樣會被產生成 CSS。
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'rounded-lg transition-all duration-200 ease-out',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
    'active:scale-[0.98]',
  ],
  {
    /*
     * 每個 variant／size 只有一個名字，不加同義別名：改樣式時只改到其中一個名字，
     * 用另一個名字的呼叫端就會默默留在舊樣式上。
     */
    variants: {
      variant: {
        /*
         * 懸停用光暈，不用 brightness-*：亮色主題的 accent 已是白字仍過 AA 的最淺值，
         * 再提亮就不合標準。光暈不改底色，兩個主題都成立。
         */
        primary: 'bg-accent text-accent-foreground hover:shadow-glow-sm',
        /* border-control 而非 border：外框按鈕的邊界是互動控制項的邊界，
           裝飾用的 border 只有 1.2:1，按鈕會看起來像一段文字。
           懸停也不能退回 border-hover——hover 態同樣受 WCAG 1.4.11 約束，
           故往上加深到 foreground。 */
        secondary:
          'bg-transparent text-foreground border border-border-control hover:bg-foreground/5 hover:border-foreground',
        /* bg-foreground/5 在兩個主題都成立：深色下是 5% 白（提亮），
           亮色下是 5% 近黑（壓暗）。換成 Tailwind 內建的白色會在亮色主題失效。 */
        ghost: 'text-foreground hover:bg-foreground/5',
        danger:
          'bg-error-muted text-error border border-error/30 hover:bg-error/20',
        link: 'text-accent underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 px-4 text-sm',
        md: 'min-h-[44px] px-5 py-2.5 text-sm',
        lg: 'min-h-[48px] px-7 py-3 text-base',
        icon: 'h-10 w-10',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
  fullWidth?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      fullWidth = false,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, className }),
          fullWidth && 'w-full',
          loading && 'cursor-not-allowed',
        )}
        ref={ref}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <svg
              className="h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
